/**
 * @tt/achievements — single shared catalog of achievement entries.
 *
 * Both `lambdas/ws-router` (which fires entries on `finalizeMatch`)
 * and the client `AchievementsCatalog` view consume this module so
 * the catalog never desyncs.
 *
 * Each entry has a `code` (stable id), display fields, a rarity, and
 * a `when()` predicate that returns true if the achievement should
 * fire after the round. Hidden achievements aren't surfaced in
 * `unlockHints` until earned.
 */
import { listEnabled, getManifest } from '@tt/games-registry';

const ENABLED_GAME_COUNT = listEnabled().length;

export type AchRarity = 1 | 2 | 3 | 4;
export type AchCategory = 'discovery' | 'skill' | 'social' | 'vanity' | 'hidden';

export interface AchCtx {
  gameId: string;
  wonThisRound: boolean;
  roundScore: number;
  /** Wins for this game BEFORE this round was applied. */
  priorWins: number;
  /** Total profile points BEFORE this round was applied. */
  priorTotal: number;
  /** Total profile points AFTER this round was applied. */
  newTotal: number;
  /** Per-game streak count after this round. */
  streakNow: number;
  /** Player's daily streak (login). */
  dailyStreak: number;
  /** Total set wins lifetime. */
  setWins: number;
  /** Total mvp / awards on this player so far this match (incl. this round). */
  mvpAwards: number;
  /** Total rounds this player has played in their lifetime AFTER this round. */
  matchesPlayedTotal: number;
  /** Total wins this player has lifetime AFTER this round. */
  winsTotal: number;
  /** Map of gameId → matchesPlayed AFTER this round. */
  perGamePlayed: Record<string, number>;
  /** Map of gameId → wins AFTER this round. Drives per-game milestone
   *  achievements (Last One Lit, Card Sharp, etc.) — added in the
   *  Phase 1 customization revamp. Optional with `?? 0` fallback so
   *  older callers that don't supply it still typecheck. */
  perGameWins?: Record<string, number>;
}

export interface AchEntry {
  code: string;
  label: string;
  category: AchCategory;
  rarity: AchRarity;
  hidden?: boolean;
  /** Plain-language description shown in the catalog. Example:
   *  "Play 50 rounds" or "Win without taking a hit." */
  hint: string;
  /** Returns numeric progress for this entry given the player's stats.
   *  When undefined, the catalog just shows the hint with no bar. */
  progress?: (c: AchCtx) => { current: number; target: number; label: string };
  /** Embers awarded on first earn (rarity-scaled). */
  embers?: number;
  /** Inventory items added when earned. Each has bucket + id. `masks` added
   *  for the mask-phantom fix (F5 masks bucket had never been wired to any
   *  reward path — see lambdas/ws-router's generic bucket TransactWrite,
   *  which needs no per-bucket code, just the type to allow it here). */
  rewards?: Array<{ bucket: 'titles' | 'emotes' | 'seatBorders' | 'seatBackgrounds' | 'seatParticles' | 'seatBgHues' | 'hats' | 'bodies' | 'palettes' | 'masks'
    // "Your Own Tavern" decor — mantel trophies + trophy-wall plaques are
    // earned-only (never purchasable), granted the same generic way.
    | 'decorMantel' | 'decorPlaque'; id: string | number }>;
  /** All achievement-granted items count as the 'play' source for the
   *  catalog dedup test. Hidden achievements use 'event'. Default 'play'. */
  source?: 'play' | 'event';
  when(c: AchCtx): boolean;
}

/** Default ember reward for an achievement at this rarity. */
export function emberReward(r: AchRarity): number {
  if (r === 1) return 75;
  if (r === 2) return 250;
  if (r === 3) return 600;
  return 1000;
}

/** Build a per-game-milestone achievement that fires on the 5th win of
 *  a single game. The reward is the game-themed title (granted once).
 *  Progress reads from `perGameWins[gameId]` so the catalog can render
 *  a bar without needing a current round to be in progress.
 *
 *  The hint/progress label use the game's display NAME (from the games
 *  registry), not the raw kebab-case id — these used to read "Win 5
 *  last-lantern rounds", leaking an internal id into player-facing text
 *  instead of "Win 5 Last Lantern rounds". */
function perGameMilestone(gameId: string, title: string, extraRewards: AchEntry['rewards'] = []): AchEntry {
  const gameName = getManifest(gameId)?.name ?? gameId;
  return {
    code: `${gameId}-5`,
    label: title,
    category: 'skill',
    rarity: 3,
    hint: `Win 5 ${gameName} rounds.`,
    progress: (c) => ({
      current: c.perGameWins?.[gameId] ?? (c.gameId === gameId ? c.priorWins + (c.wonThisRound ? 1 : 0) : 0),
      target: 5,
      label: `${gameName} wins`,
    }),
    rewards: [
      { bucket: 'titles', id: title },
      ...(extraRewards ?? []),
    ],
    // priorWins is per-current-game, so this only fires while the
    // player is mid-`gameId` round AND they had ≥4 wins beforehand.
    when: (c) => c.gameId === gameId && c.wonThisRound && c.priorWins >= 4,
  };
}

export const ACHIEVEMENTS_V2: AchEntry[] = [
  // ── Discovery (just play)
  { code: 'play-1',       label: 'First Drink',     category: 'discovery', rarity: 1, hint: 'Play 1 round.',
    progress: (c) => ({ current: c.matchesPlayedTotal, target: 1,  label: 'rounds played' }),
    when: (c) => c.matchesPlayedTotal >= 1 },
  { code: 'play-10',      label: 'Regular',         category: 'discovery', rarity: 1, hint: 'Play 10 rounds.',
    progress: (c) => ({ current: c.matchesPlayedTotal, target: 10, label: 'rounds played' }),
    // "Your Own Tavern" — trophy-wall plaque (r1, earned not bought).
    rewards: [{ bucket: 'decorPlaque', id: 'plaque-bronze' }],
    when: (c) => c.matchesPlayedTotal >= 10 },
  { code: 'play-50',      label: 'Veteran',         category: 'discovery', rarity: 2, hint: 'Play 50 rounds.',
    progress: (c) => ({ current: c.matchesPlayedTotal, target: 50, label: 'rounds played' }),
    // seat-background-library cited this achievement in the catalog but was
    // never actually granted by it — wiring it here per the catalog's intent.
    rewards: [{ bucket: 'palettes', id: 'palette-bone' }, { bucket: 'seatBackgrounds', id: 'library' }],
    when: (c) => c.matchesPlayedTotal >= 50 },
  { code: 'play-200',     label: 'Patron',          category: 'discovery', rarity: 3, hint: 'Play 200 rounds.',
    progress: (c) => ({ current: c.matchesPlayedTotal, target: 200, label: 'rounds played' }),
    rewards: [{ bucket: 'bodies', id: 100 }], // Floating Orb archetype
    when: (c) => c.matchesPlayedTotal >= 200 },
  { code: 'play-all-games', label: 'Sampler',       category: 'discovery', rarity: 2, hint: 'Play every game at least once.',
    progress: (c) => ({ current: Object.keys(c.perGamePlayed).length, target: ENABLED_GAME_COUNT, label: 'games played' }),
    when: (c) => Object.keys(c.perGamePlayed).length >= ENABLED_GAME_COUNT },
  { code: 'daily-1',      label: 'Daily Visitor',   category: 'discovery', rarity: 1, hint: 'Log in 1 day.',
    progress: (c) => ({ current: c.dailyStreak, target: 1, label: 'daily streak' }),
    when: (c) => c.dailyStreak >= 1 },
  { code: 'daily-7',      label: 'Daily Devotee',   category: 'discovery', rarity: 2, hint: 'Daily streak of 7.',
    progress: (c) => ({ current: c.dailyStreak, target: 7, label: 'daily streak' }),
    when: (c) => c.dailyStreak >= 7 },
  { code: 'daily-30',     label: 'Hearthkeeper',    category: 'discovery', rarity: 4, hint: 'Daily streak of 30.', embers: 1500,
    progress: (c) => ({ current: c.dailyStreak, target: 30, label: 'daily streak' }),
    when: (c) => c.dailyStreak >= 30 },

  // ── Skill (game-agnostic)
  { code: 'win-1',        label: 'First Winner',    category: 'skill', rarity: 1, hint: 'Win 1 round.',
    progress: (c) => ({ current: c.winsTotal, target: 1, label: 'round wins' }),
    when: (c) => c.winsTotal >= 1 },
  { code: 'win-10',       label: 'Round Hunter',    category: 'skill', rarity: 2, hint: 'Win 10 rounds.',
    progress: (c) => ({ current: c.winsTotal, target: 10, label: 'round wins' }),
    // "Your Own Tavern" — trophy-wall plaque (r2, earned not bought).
    rewards: [{ bucket: 'decorPlaque', id: 'plaque-silver' }],
    when: (c) => c.winsTotal >= 10 },
  { code: 'win-50',       label: 'Round King',      category: 'skill', rarity: 3, hint: 'Win 50 rounds.',
    progress: (c) => ({ current: c.winsTotal, target: 50, label: 'round wins' }),
    when: (c) => c.winsTotal >= 50 },
  { code: 'set-1',        label: 'Table Champion',  category: 'skill', rarity: 2, hint: 'Win a full set.',
    progress: (c) => ({ current: c.setWins, target: 1, label: 'set wins' }),
    // "Your Own Tavern" — mantel trophy (r3, earned not bought).
    rewards: [{ bucket: 'decorMantel', id: 'golden-tankard' }],
    when: (c) => c.setWins >= 1 },
  { code: 'set-5',        label: 'Set Collector',   category: 'skill', rarity: 3, hint: 'Win 5 sets.',
    progress: (c) => ({ current: c.setWins, target: 5, label: 'set wins' }),
    // "Your Own Tavern" — trophy-wall plaque (r3, earned not bought).
    rewards: [{ bucket: 'decorPlaque', id: 'plaque-gold' }],
    when: (c) => c.setWins >= 5 },
  { code: 'set-25',       label: 'Undefeated',      category: 'skill', rarity: 4, hint: 'Win 25 sets.',
    progress: (c) => ({ current: c.setWins, target: 25, label: 'set wins' }),
    // "Your Own Tavern" — mantel trophy (r5, the mantel flagship).
    rewards: [{ bucket: 'decorMantel', id: 'phoenix-cup' }],
    when: (c) => c.setWins >= 25 },
  { code: 'club-100',     label: '100 Club',        category: 'skill', rarity: 1, hint: 'Reach 100 profile points.',
    progress: (c) => ({ current: c.newTotal, target: 100, label: 'profile points' }),
    when: (c) => c.priorTotal < 100  && c.newTotal >= 100 },
  { code: 'club-1k',      label: '1000 Club',       category: 'skill', rarity: 2, hint: 'Reach 1,000 profile points.',
    progress: (c) => ({ current: c.newTotal, target: 1000, label: 'profile points' }),
    when: (c) => c.priorTotal < 1000 && c.newTotal >= 1000 },
  { code: 'club-10k',     label: '10K Club',        category: 'skill', rarity: 4, hint: 'Reach 10,000 profile points.', embers: 1500,
    progress: (c) => ({ current: c.newTotal, target: 10000, label: 'profile points' }),
    when: (c) => c.priorTotal < 10000 && c.newTotal >= 10000 },
  { code: 'streak-3',     label: 'Hot Streak',      category: 'skill', rarity: 1, hint: 'Win 3 rounds of the same game in a row.',
    progress: (c) => ({ current: c.streakNow, target: 3, label: 'win streak' }),
    // seat-particle-shadows AND mask-phantom both cited this achievement in
    // the catalog but neither was ever actually granted by it — wiring both
    // here per the catalog's stated intent (multiple rewards on one entry).
    rewards: [{ bucket: 'seatParticles', id: 'shadows' }, { bucket: 'masks', id: 'phantom' }],
    when: (c) => c.streakNow >= 3 },
  { code: 'streak-5',     label: 'White Hot',       category: 'skill', rarity: 2, hint: 'Win 5 rounds of the same game in a row.',
    progress: (c) => ({ current: c.streakNow, target: 5, label: 'win streak' }),
    when: (c) => c.streakNow >= 5 },
  { code: 'streak-10',    label: 'Inferno',         category: 'skill', rarity: 4, hint: 'Win 10 rounds of the same game in a row.',
    progress: (c) => ({ current: c.streakNow, target: 10, label: 'win streak' }),
    // seat-particle-lightning cited this achievement but was never actually
    // granted by it — wiring it here per the catalog's stated intent.
    rewards: [{ bucket: 'bodies', id: 102 }, { bucket: 'seatParticles', id: 'lightning' }], // Phantom archetype + Lightning particles
    when: (c) => c.streakNow >= 10 },
  { code: 'win-each-game', label: 'Eclectic',       category: 'skill', rarity: 3, hint: 'Win at least one round in every game.',
    progress: (c) => ({ current: Object.values(c.perGameWins ?? {}).filter((wins) => wins > 0).length, target: ENABLED_GAME_COUNT, label: 'games won' }),
    rewards: [{ bucket: 'bodies', id: 101 }], // Eight-Legged archetype
    when: (c) => Object.values(c.perGameWins ?? {}).filter((wins) => wins > 0).length >= ENABLED_GAME_COUNT },

  // ── Social (4-player table presence)
  { code: 'mvp-1',        label: 'Spotlight',       category: 'social', rarity: 1, hint: 'Earn 1 MVP / award.',
    progress: (c) => ({ current: c.mvpAwards, target: 1, label: 'awards' }),
    when: (c) => c.mvpAwards >= 1 },
  { code: 'mvp-10',       label: 'MVP Magnet',      category: 'social', rarity: 3, hint: 'Earn 10 awards.',
    progress: (c) => ({ current: c.mvpAwards, target: 10, label: 'awards' }),
    when: (c) => c.mvpAwards >= 10 },
  { code: 'mvp-50',       label: 'Stage Light',     category: 'social', rarity: 4, hint: 'Earn 50 awards.',
    progress: (c) => ({ current: c.mvpAwards, target: 50, label: 'awards' }),
    rewards: [{ bucket: 'emotes', id: '✨' }],
    when: (c) => c.mvpAwards >= 50 },

  // ── Vanity / silly
  { code: 'win-from-bottom', label: 'Comeback Kid', category: 'vanity', rarity: 2, hint: 'Win a round after playing 5+ rounds with zero per-game wins.',
    when: (c) => c.wonThisRound && c.priorWins === 0 && c.matchesPlayedTotal >= 5 },
  // Donut also grants the skull emote (a darkly-funny reward for ending
  // a round with 0). Per spec §3.6 this is the "achievement-only" path
  // for skull — the player who whiffs gets the keepsake.
  { code: 'no-score-zero', label: 'Donut',          category: 'vanity', rarity: 1, hint: 'End a round with a score of zero.',
    rewards: [{ bucket: 'emotes', id: '💀' }],
    when: (c) => !c.wonThisRound && c.roundScore === 0 },

  // ── MVP Magnet promoted to grant a sparkles emote on first earn.
  // (Original entry above already exists; this is a duplicate intent.
  //  The reward is added to the existing entry below via the patch
  //  step — the duplicate-prevention test relies on `code` being unique
  //  across this list. Keep the original entry as the single source.)

  // ── Per-game milestone achievements: 5 wins of a single game grants
  // a game-themed title. Picks priorWins+1 (i.e. fires on the 5th win)
  // and includes a progress fn so the catalog renders a bar. Per spec
  // §3.10, these are r3 rewards. Embers default-scale to 600.
  perGameMilestone('last-lantern',   'Last One Lit'),
  perGameMilestone('draft-duel',     'Card Sharp'),
  perGameMilestone('stick-brawl',    'Thunderclap'),
  perGameMilestone('burnstile',      'Heat Stroke'),
  perGameMilestone('climb-spire',    'Spire Climber'),
  // ouija-roulette: 5 wins also grants the Emerald palette (§3.10).
  perGameMilestone('ouija-roulette', 'Spirit Speaker',
    [{ bucket: 'palettes', id: 'palette-emerald' }]),
  // hexagon-heat: 5 wins also grants the Hexagon body archetype (r4) +
  // the "Your Own Tavern" Crystal Hex mantel trophy (r4).
  perGameMilestone('hexagon-heat',   'Hex Master',
    [{ bucket: 'bodies', id: 105 }, { bucket: 'decorMantel', id: 'crystal-hex' }]),
  perGameMilestone('coin-conveyer',  'Goldhand'),
  perGameMilestone('spice-rush',     'Spicemaster'),
  perGameMilestone('witch-switch',   'Truth Seer'),
  perGameMilestone('trace-race',     'Steady Hand'),
  perGameMilestone('one-of-us',      'Wolf in the Fold'),
  perGameMilestone('quick-draw',     'First Pen'),
  perGameMilestone('tap-race',       'Quickfinger'),
  perGameMilestone('tarot-tap',      'Card Whisperer'),
  perGameMilestone('lumber-tumble',  'Bridge Runner'),
  perGameMilestone('banner-bash',     'Banner Baron',
    [{ bucket: 'seatBorders', id: 'banner' }]),
  perGameMilestone('cellar-sweep',    'Cellar Captain'),
  perGameMilestone('cipher-cellar',   'Cipher Sage'),
  perGameMilestone('heist-heist',     'Vault Ghost'),
  perGameMilestone('phantom-pursuit', 'Phantom Mark',
    [{ bucket: 'seatParticles', id: 'wisps' }]),
  perGameMilestone('pirate-pulley',   'Deckhand Ace'),
  perGameMilestone('plinko-tavern',   'Peg Whisperer'),
  perGameMilestone('quill-race',      'Quillmaster'),
  perGameMilestone('relay-forge',     'Forge Captain'),
  // rope-walk: 5 wins also grants the "Your Own Tavern" Silver Rope mantel
  // trophy (r3) — the team lead's spec verified this per-game milestone
  // exists (code = 'rope-walk-5') so no win-10 fallback was needed.
  perGameMilestone('rope-walk',       'Ropewalker',
    [{ bucket: 'decorMantel', id: 'silver-rope' }]),
  perGameMilestone('sky-sieve',       'Sky Sifter'),
  perGameMilestone('splatter-square', 'Paint Baron',
    [{ bucket: 'seatBackgrounds', id: 'splatter' }]),
  perGameMilestone('stack-smack',     'Stack Boss'),
  perGameMilestone('trapline',        'Trapwright'),

  // ── Hidden (don't appear in catalog until earned)
  { code: 'witching-hour', label: 'Witching Hour',  category: 'hidden', rarity: 3, hidden: true, hint: '???',
    // seat-border-runic-engraved cited this achievement but was never
    // actually granted by it (it had no rewards array at all) — wiring it
    // here per the catalog's stated intent.
    rewards: [{ bucket: 'seatBorders', id: 'runic-engraved' }],
    source: 'event',
    when: () => { const h = new Date().getHours(); return h >= 0 && h < 1; } },
  { code: 'dawn-streak',   label: 'Dawnkeeper',     category: 'hidden', rarity: 4, hidden: true, hint: '???', embers: 1500,
    rewards: [{ bucket: 'bodies', id: 104 }], // Living Flame archetype
    source: 'event',
    when: (c) => { const h = new Date().getHours(); return c.dailyStreak >= 5 && h >= 5 && h < 7; } },
  { code: 'big-win',       label: 'Sweeper',        category: 'hidden', rarity: 4, hidden: true, hint: '???',
    when: (c) => c.streakNow >= 5 && c.wonThisRound },
];
